import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import {
  IDeleteParams,
  IListParams,
  IPaginationState,
  IScrollPosition,
  IStoreProps,
} from 'src/app/talent-litmus-core/common/common.interface';
import { ICharacter } from './character.interface';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl1-characters';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<ICharacter, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class CharacterCoreService {
  private _loading = false;

  /**
   * @name charactersList$
   * @description emits stored list of the characters
   */

  charactersList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 300);
    } else {
      this.refresh();
    }
  }

  public getCharacters(params?: IListParams): void {
    this._loading = true;
    this._api
      .get('characters/list', params || {})
      .subscribe((data: ICharacterResponseList) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addCharacter(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('characters/add', dataInput || {}).pipe(
      tap((data: ICharacter) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateCharacter(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('characters/edit', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: ICharacter) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteCharacter(dataInput: IDeleteParams): Observable<any> {
    this._loading = true;
    return this._api.delete('characters', { body: dataInput }).pipe(
      tap(() => {
        store.update(deleteEntities(dataInput['_id']));
        this._loading = false;
      }),
    );
  }

  public getCharacterInfo(id: string): ICharacter {
    const character = store.query(getEntity(id));
    return character;
  }

  public resolveCharacterId(character: string | ICharacter): string {
    let characterId: string | ICharacter = '';
    if (character) {
      characterId = character;
    }
    if (character['_id']) {
      characterId = character['_id'];
    }
    return characterId as string;
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getCharacters();
    }
  }
}

interface ICharacterResponseList {
  list: ICharacter[];
  totalCount: number;
}
